import type { ComponentType } from 'react';
import { createBrowserRouter, Navigate, Outlet, useRouteError } from 'react-router';
import { useTranslation } from 'react-i18next';
import { RefreshCw, TriangleAlert } from 'lucide-react';
import { EmptyState } from './components/common';
import { RequireAuth, RootLayout } from './components/Layout';
import HomePage from './pages/HomePage';
import NotFoundPage from './pages/NotFoundPage';

function page<T extends Record<string, unknown>>(load: () => Promise<T>, name: keyof T = 'default') {
  return async () => ({ Component: (await load())[name] as ComponentType });
}

function RouteError() {
  const { t } = useTranslation();
  console.error(useRouteError());
  return (
    <EmptyState
      icon={<TriangleAlert className="size-7" />}
      title={t('errors.generic')}
      action={
        <button type="button" className="btn-primary" onClick={() => window.location.reload()}>
          <RefreshCw className="size-4" /> {t('common.retry')}
        </button>
      }
    />
  );
}

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <RouteError />,
    children: [
      {
        errorElement: <RouteError />,
        children: [
          { index: true, element: <HomePage /> },
          { path: 'recipes/:id/:slug?', lazy: page(() => import('./pages/recipe/RecipePage')) },
          { path: 'login', lazy: page(() => import('./pages/auth/LoginPage')) },
          { path: 'verify-email', lazy: page(() => import('./pages/auth/EmailPages'), 'VerifyEmailPage') },
          { path: 'forgot-password', lazy: page(() => import('./pages/auth/EmailPages'), 'ForgotPasswordPage') },
          { path: 'reset-password', lazy: page(() => import('./pages/auth/EmailPages'), 'ResetPasswordPage') },
          {
            element: (
              <RequireAuth>
                <Outlet />
              </RequireAuth>
            ),
            children: [
              { path: 'new', lazy: page(() => import('./pages/NewRecipePage')) },
              { path: 'save', lazy: page(() => import('./pages/SavePage')) },
              { path: 'recipes/new', lazy: page(() => import('./pages/editor/EditorPage')) },
              { path: 'recipes/:id/edit', lazy: page(() => import('./pages/editor/EditorPage')) },
              { path: 'lists', element: <Navigate to="/lists/shopping" replace /> },
              { path: 'lists/:listKey', lazy: page(() => import('./pages/ListsPage')) },
              { path: 'my-recipes', lazy: page(() => import('./pages/MyRecipesPage')) },
              { path: 'account', lazy: page(() => import('./pages/AccountPage')) },
            ],
          },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);
